<template>
  <section class="s-flexdrive-vehicle">
    <div class="page-container">
      <div class="s-flexdrive-vehicle__gallery">
        <VehicleGallerySlick
          v-if="recordsGallery.length"
          :records="recordsGallery" />
        <div v-if="carObj?.isNew" class="e-new"><NewLottie /></div>
        <div v-if="recordsGallery.length" class="d-flex justify-content-end">
          <small>
            <i>{{ dictionary.galleryDisclaimer }}</i>
          </small>
        </div>
      </div>

      <div class="s-flexdrive-vehicle__header mb-80">
        <div v-if="highlightText" class="s-flexdrive-vehicle__tagged">
          <span>{{ highlightText }}</span>
        </div>

        <div class="row">
          <div class="col-lg-6 col-md-6 mb-40 mb-lg-0">
            <h1 class="h1 extrabold mb-5">
              {{ carObj?.make }} {{ carObj?.model }}
              <span v-if="!carObj?.isGuaranteed">
                {{ dictionary.orSimilarText }}
              </span>
            </h1>
            <h2 class="h4">
              {{ dictionary.modelDescriptionLabel }}
              {{ carObj?.modelDescription }}
            </h2>
          </div>
          <div
            class="col-lg-3 col-md-6 mb-40 mb-lg-0 s-flexdrive-vehicle__price">
            <div
              class="b-vehicle__price-w-vat"
              :class="{ 'is-active': vatIncluded }">
              <div class="bold mb-5">
                <span>{{ dictionary.vehiclePricePrefix }}&nbsp;</span>
                <!-- prettier-ignore -->
                <span v-if="carObj?.startingPricePerMonthInteger && !vatIncluded" class="e-discount-price">€{{carObj?.startingPricePerMonthInteger.toLocaleString(lang)}}</span>
                <!-- prettier-ignore -->
                <span v-if="carObj?.startingPricePerMonthInclVatInteger  && vatIncluded" class="e-discount-price">€{{ carObj?.startingPricePerMonthInclVatInteger.toLocaleString(lang) }}</span>
                <span v-if="carObj?.startingPricePerMonthInteger">&nbsp;</span>
                <span class="animate">
                  <!-- prettier-ignore -->
                  <em v-if="vatIncluded">€{{ carObj?.pricePerMonthInclVatInteger.toLocaleString(lang) }}
                  </em>
                  <!-- prettier-ignore -->
                  <em v-else>€{{ carObj?.pricePerMonthInteger.toLocaleString(lang) }}</em>
                  {{ dictionary.vehiclePriceSuffix }}
                </span>
              </div>

              <div class="form-check form-switch">
                <label class="form-check-label" for="vatIncluded">
                  <span v-if="vatIncluded">
                    {{ dictionary.vehicleVatIncluded }}
                  </span>
                  <span v-else>{{ dictionary.vehicleVatExcluded }}</span>
                </label>
                <input
                  v-if="!isDisabled"
                  id="vatIncluded"
                  v-model="vatIncluded"
                  class="form-check-input"
                  type="checkbox"
                  role="switch" />
              </div>
            </div>
          </div>
          <div class="col-lg-3 s-flexdrive-vehicle__button">
            <button
              type="button"
              class="btn btn-primary btn-full-width"
              @click="onSubmit">
              {{ dictionary.buttonVehicleViewMoreText }}
            </button>
            <div class="mt-10">
              <Anchor
                v-if="editVehicleLink"
                :item="editVehicleLink"
                :hide-text="true"
                class="col btn btn-link btn-icon-start btn-edit">
                <i class="icon-edit"></i>
                <small class="extrabold">{{ editVehicleLink.text }}</small>
              </Anchor>
            </div>
          </div>
        </div>
      </div>

      <FlexVehicleTechs v-if="carObj" :item="carObj" />

      <VehicleBenefits
        v-if="vehicleBenefits.length"
        :records="vehicleBenefits"
        :columns="4"
        class="d-none d-lg-block mb-40" />

      <Accordion
        v-if="vehicleBenefits.length"
        :title="dictionary.benefitsTitle"
        class="d-block d-lg-none mb-40">
        <VehicleBenefits
          :records="vehicleBenefits"
          :columns="4"
          :hide-title="true" />
      </Accordion>

      <Anchor
        v-if="clickToCall"
        :item="clickToCall"
        :hide-text="true"
        aria-label="Click to call"
        class="e-call-us body-fixed">
        <i class="icon-phone-call"></i>
      </Anchor>
    </div>
  </section>
</template>
<script>
import * as lib from './../../lib/lib'
import FlexVehicleTechs from './../components/flexdrive/FlexVehicleTechs.vue'
import VehicleBenefits from './../components/VehicleBenefits.vue'
import VehicleGallerySlick from './../components/leaseitnow/VehicleGallerySlick.vue'
import Anchor from './../components/common/Anchor.vue'
import { v4 as uuidv4 } from 'uuid'
import NewLottie from './../components/lotties/New.vue'
import Accordion from './../components/common/Accordion.vue'

export default {
  components: {
    Accordion,
    Anchor,
    FlexVehicleTechs,
    VehicleBenefits,
    VehicleGallerySlick,
    NewLottie,
  },
  data() {
    return {
      // data
      vatIncluded: false,

      carObj: null,
      vehicleBenefits: [],
      highlightBgColor: '',
      highlightText: '',

      title: '',
      step: 1,
      steps: [],
      editVehicleLink: null,
      lang: 'en',

      clickToCall: null,
    }
  },
  computed: {
    isDisabled() {
      if (!this.carObj) {
        return true
      }
      if (!this.carObj.pricePerMonthInclVat) {
        return true
      }

      return false
    },

    recordsGallery() {
      if (!this.carObj) {
        return []
      }

      return this.carObj.images.map((x) => ({
        id: uuidv4(),
        thumbnail: { url: x.url, alt: '' },
        image: { url: x.url, alt: '' },
        isNew: x.isNew,
        offerText: x.offerText,
      }))
    },
  },
  mounted() {
    const {
      title,
      steps,
      vehicle,
      vehicleBenefits,
      editVehicleLink,
      clickToCall,
    } = lib.getData('flexDriveVehicleData')
    this.vehicleBenefits = vehicleBenefits

    const urlHashAdded = { url: `${editVehicleLink.url}#selectVehicle` }
    this.editVehicleLink = { ...editVehicleLink, ...urlHashAdded }

    this.title = title
    this.steps = steps

    this.carObj = vehicle

    this.highlightBgColor = vehicle.highlightBgColor
    this.highlightText = vehicle.highlightText

    this.clickToCall = clickToCall
    this.lang = lib.getLocaleFromLangAttr()
  },
  methods: {
    onSubmit() {
      let link = this.steps[2].link.url
      if (this.vatIncluded) {
        link += '&vat=1'
      }
      location.href = link
    },
    onToggleVat() {
      this.vatIncluded = !this.vatIncluded
    },
    onClick(item, index) {
      if (this.hasLink(item, index)) {
        window.location.assign(item.link.url)
      }
    },

    hasLink(item, index) {
      return (
        lib.hasValue(item.link?.url) &&
        this.isPrevious(index) &&
        this.step + 1 !== this.steps.length
      )
    },
    getClassName(item, index) {
      const classNames = []

      if (this.isActive(index)) {
        classNames.push('is-active')
      }

      if (this.isPrevious(index)) {
        classNames.push('is-previous')
      }

      if (this.hasLink(item, index)) {
        classNames.push('has-link')
      }

      return classNames.join(' ')
    },
    isActiveBar(index) {
      return this.step >= index + 1
    },
    isActive(index) {
      return index === Number(this.step)
    },
    isPrevious(index) {
      return index < Number(this.step)
    },
  },
}
</script>
<style lang="scss" scoped>
.s-flexdrive-vehicle__tagged {
  background-color: v-bind(highlightBgColor);
  color: white;
}
</style>
